import { LOCALE_ID, Pipe, PipeTransform, inject } from '@angular/core';
import { formatPercent } from '@angular/common';
import { Decimal } from 'decimal.js';

/**
 * Percent
 *
 * Formats a fraction as a percentage, like Angular's `percent` pipe, and
 * also accepts a decimal.js Decimal (the value of a Decimal-backed base type).
 */
@Pipe({
  name: 'percent',
  standalone: true,
})
export class PercentPipe implements PipeTransform {
  locale: string = inject(LOCALE_ID);

  transform(
    value: Decimal | number | string | null | undefined,
    digitsInfo?: string,
    locale?: string,
  ): string | null {
    // An empty value shows nothing, as with Angular's pipe
    if (value == null || value === '') return null;

    // Text that isn't a number is a template error, as with Angular's pipe
    let fraction: Decimal;
    try {
      // Read the value as an exact Decimal
      fraction = new Decimal(value);
    } catch {
      // Report the value Angular's pipe would have rejected
      throw new Error(`InvalidPipeArgument: '${value}' for pipe 'PercentPipe'`);
    }

    /**
     * Round the percentage with Decimal before formatting
     *
     * The percentage is rounded to the digits it will show, then handed back
     * to Angular as a fraction. Angular shifts the digits rather than
     * multiplying, so the value it shows is the rounded one exactly.
     */
    const percentage = fraction
      .times(100)
      .toDecimalPlaces(
        this.maxFractionDigits(digitsInfo),
        Decimal.ROUND_HALF_UP,
      );
    return formatPercent(
      percentage.div(100).toNumber(),
      locale || this.locale,
      digitsInfo,
    );
  }

  maxFractionDigits(digitsInfo?: string): number {
    // Without a readable digitsInfo, the default stands
    const defaultMax = 0;
    const parts = digitsInfo?.match(/^(\d+)?\.((\d+)(-(\d+))?)?$/);
    if (!parts) return defaultMax;

    // An explicit maximum wins
    if (parts[5] != null) return parseInt(parts[5], 10);

    // A minimum above the default raises the maximum with it
    if (parts[3] != null) return Math.max(parseInt(parts[3], 10), defaultMax);

    // Otherwise the default stands
    return defaultMax;
  }
}
